import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdClose } from "react-icons/md";
import { IoNotificationsOutline } from "react-icons/io5";
import { customStringify } from "../../utils.js";
import { FormattedMessage, useIntl } from "react-intl";
import { network_tl } from "../../translations";
import Loading from "../Loading";
import _ from "lodash";
import { Link } from "react-router-dom";
import { set_global_notifications } from "../../redux/slices/globalNotificationsSlice.js";
import useApi from "../../hooks/useApi/index.jsx";
import NotificationItem from "./NotificationItem.js";
import { LISTING_LIMIT } from "../../constants.js";
import { BiLoaderAlt } from "react-icons/bi";
import axios from "axios";

const NotificationSidebar = () => {
  const dispatch = useDispatch();
  const { sidebar, data, sidebar_api_data, unread_count } = useSelector((state) => state.global_notifications);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const sidebarRef = useRef(null);
  const intl = useIntl();
  const { Axios } = useApi();

  const getData = async (reset = false) => {
    try {
      const currentOffset = reset ? 0 : offset;
      setLoadingMore(!reset);
      setLoading(reset);
      setError(false);

      const result = await Axios.get(`api/chat/get-notification?limit=${LISTING_LIMIT}&offset=${currentOffset}`);

      const newNotifications = result.data.payload.messages || [];
      const newTotalCount = result.data.count || 0;
      
      dispatch(
        set_global_notifications({
          sidebar_api_data: reset 
            ? newNotifications 
            : [...(reset ? [] : sidebar_api_data), ...newNotifications],
          not_seen: result.data.payload.not_seen || 0,
          unread_count: result.data.payload.unread_count || 0
        })
      );

      setTotalCount(newTotalCount);
      // Calculate if there are more notifications to load
      const loadedCount = reset ? newNotifications.length : sidebar_api_data.length + newNotifications.length;
      setHasMore(loadedCount < newTotalCount);
      
      setOffset(reset ? LISTING_LIMIT : currentOffset + LISTING_LIMIT);
    } catch (e) {
      if (axios.isCancel(e)) {
        
      }
      else if (e.response) {
        setError(customStringify(e.response.data.description))
      } else {
        setError(network_tl(intl))
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
      getData(true);
  }, []);

  const handleScroll = useCallback(() => {
    if (!sidebarRef.current || loading || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = sidebarRef.current;
    const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 100; 

    if (isNearBottom) {
      getData();
    }
  }, [loading, loadingMore, hasMore, sidebar_api_data]);

  useEffect(() => {
    const currentRef = sidebarRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
      return () => {
        if (currentRef) {
          currentRef.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [handleScroll]);

  return (
    <>
      <div
        ref={sidebarRef}
        className={`fixed top-0 right-0 ${
          !sidebar ? "transition-transform translate-x-full" : "transition-transform"
        } z-[1002] w-80 h-screen py-4 overflow-y-auto bg-white`}
        tabIndex="-1"
      >
        <button
          type="button"
          onClick={() => dispatch(set_global_notifications({ sidebar: false }))}
          className="bg-transparent hover:bg-primary hover:text-white rounded-full p-2 text-sm absolute left-3 top-3 inline-flex items-center"
        >
          <MdClose className="text-2xl" />
        </button>

        <div className="mt-12">
          {loading && !error ? (
            <Loading />
          ) : error ? (
            <p className="text-red-500 font-medium text-sm text-center">
              {error}
            </p>
          ) : !loading && !error && (data.length > 0 || sidebar_api_data.length > 0) ? (
            <div className="pb-4 border-t border-gray-200">
              <div className="mb-4">
                {sidebar_api_data.map((item, index) => (
                  <NotificationItem
                    payload={item}
                    key={`${item.id}-${index}`}
                    type={item.type}
                    loading={loading}
                    setLoading={setLoading}
                  />
                ))}
              </div>

              {loadingMore && (
                <div className="flex justify-center py-2">
                  <BiLoaderAlt className='mx-auto text-xl text-primary animate-spin'/>
                </div>
              )}

              {!hasMore && sidebar_api_data.length > 0 && (
                <p className="text-center text-sm text-gray-500 py-2">
                  <FormattedMessage id="no_more_notifications" defaultMessage="No more notifications" />
                </p>
              )}

              {/* <Link
                onClick={() => {
                  dispatch(set_global_notifications({ sidebar: false }))
                }}
                to="/notifications"
                className="py-2 px-4 mx-8 flex items-center bg-primary rounded-full text-white justify-center transition-all hover:shadow-lg hover:border-transparent text-center uppercase font-medium text-sm border-b-2 border-gray-200"
              >
                <FormattedMessage id="show_all_notification" defaultMessage={'Show All notifications'}/>
              </Link> */}
            </div>
          ) : !loading && !error && data.length === 0 && sidebar_api_data.length === 0 ? (
            <div className="h-[50vh] flex flex-col items-center justify-center">
              <IoNotificationsOutline className="text-2xl text-gray-700" />
              <p className="mt-2 capitalize text-sm font-medium text-gray-700">
                <FormattedMessage id="new_notification_here" defaultMessage={"New notifications will appear here"}/>
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {sidebar && (
        <div
          onClick={() => dispatch(set_global_notifications({ sidebar: false }))}
          className="block lgpad:hidden bg-gradient-to-t from-black via-black/70 to-black/50 fixed top-0 left-0 right-0 bottom-0 z-[1001]"
        />
      )}
    </>
  );
};

export default NotificationSidebar;